// primitives.ts — client-side ports of the READ-display render_list_display_*
// engine functions (includes/functions/janus/core/list_display_helpers.php).
// Each returns an HTML string (the PHP echoed directly); the driver concatenates.
//
// The same primitives serve read cards and the raw PHP mounts. Form adapters
// supply choice/row callbacks without importing another bundle's state.

import {
    type ReferencedNames,
    layerSlot, layerEntriesOf, layerOf, itemsCol, layerValue, computedValue, primaryPictureOf,
    hasLayer, topSlot0,
    layerScoringToken, numericSlot, scalarLayerSlot, slotString,
    ucwordsAndSpaceTs, headerIconHtml, nameOf, afterLabelSep, isUuid, unresolvedReferenceHtml,
    formatQty, displaySpellCastingTime, showRequiresClassLevelIcons, skillTierBadges, iconForRef, refHasOwnIcon,
    HIDE_REQUIRES_LEVEL, ICON_BEFORE_NAME_ITEM_TYPES, sortLayerEntriesByRequirements,
} from './helpers';
import type { FullNodeIndex } from '../shared/array-types';

type UnknownRecord = Record<string, unknown>;
export interface RenderBlob extends UnknownRecord {
    item_layer_types?: UnknownRecord;
    catalog?: UnknownRecord;
    inventory?: UnknownRecord;
    inventory_id?: unknown;
    encounter_creatures?: UnknownRecord;
    computed?: UnknownRecord & {
        creature?: UnknownRecord & { natural_weapon?: unknown; skill_ability?: UnknownRecord };
        enchantment?: UnknownRecord & { abilities?: UnknownRecord };
        // UUID(upper) → the PL this item pays to grant that permanent status (PlCost
        // status_effect_permanent, skill-tier discount applied). Stamped by
        // calculate_status_effect_modifiers; see the read in renderCardListItemLayerRead.
        status_effect_permanent_pl?: UnknownRecord;
    };
}

export interface RenderCtx {
    arr: RenderBlob;
    names: ReferencedNames;
    // Equipment-slot grid HTML, rendered client-side from the blob by the inventory bundle
    // (window.renderEquipmentSlotGrid — inventory→TS Phase 2). Threaded in by the mount glue
    // (index.ts) / compare.ts; '' when nothing is equipped.
    equipmentSlotsHtml?: string;
    // UUID(upper) → the FULL in-blob copy of an ability/spell (the node carrying the richest
    // item_layer_types). The dedup pass ([[project_full_nest_global_dedup]]) keeps each ability
    // full-nested ONCE and shells it everywhere else, and skill-derived abilities are surfaced
    // into computed.creature.skill_ability as lean shells (ft_surface_ability_into_computed) — so
    // the ability LIST iterates shells with no mechanics, and the rune/badges would render empty.
    // The full copy lives elsewhere in the SAME clean blob (e.g. under skill_tier_N), so we index
    // it once (buildFullAbilityIndex) and resolve shells against it — pure blob data, no re-query.
    fullById?: FullNodeIndex;
    // Set once buildItemListDisplayHtml has folded the blob `.catalog` name gaps into `names`
    // (mergeNamesWithCatalog) — guards the one-time enrichment against scoped-ctx re-entry.
    namesCatalogMerged?: boolean;
    formClassLevel?: number;
    renderRows?: typeof renderCardListItemLayerRead;
    renderChoice?: (ctx: RenderCtx, choices: unknown[], suffix: string, layer: string, source: string) => string;
}

function recordOf(value: unknown): UnknownRecord | null {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
        ? value as UnknownRecord
        : null;
}

export type ContentType =
    | 'badge' | 'badge_percent' | 'badge_percent_suffix'
    // 2026-09-04: the unit-suffixed twins of badge_percent_suffix, so a distance layer reads
    // "120 ft" and a multiplier reads "2x" instead of a bare number the reader has to interpret.
    | 'badge_ft_suffix' | 'badge_multiplier_suffix'
    | 'item_link' | 'item_link_with_name' | 'label_only' | 'text' | 'custom';

export interface SingleItemOpts {
    label?: string | null;
    labelHtml?: boolean;
    liClass?: string;
    contentType?: ContentType;
    badgeClass?: string;
    customHtml?: string;
    contentCallback?: (ctx: RenderCtx, layer: string) => string;
    extraHtml?: string;
    extraHtmlCallback?: (ctx: RenderCtx, layer: string) => string;
    skipItemTypes?: string[];
    icon?: string | false | null;
    requireLayers?: string[];
}

function itemType(arr: unknown): string | null {
    const t = itemsCol<string>(arr, 'item_type');
    return t === undefined ? null : t;
}

/** Port of render_list_display_single_item (read path). */
export function renderSingleItem(ctx: RenderCtx, layerName: string, opts: SingleItemOpts = {}): string {
    const arr = ctx.arr;

    const requireLayers = opts.requireLayers ?? null;
    if (requireLayers) {
        for (const l of requireLayers) {
            if (!hasLayer(arr, l)) return '';
        }
    } else if (!hasLayer(arr, layerName)) {
        return '';
    }

    const skip = opts.skipItemTypes ?? null;
    if (skip && itemType(arr) !== null && skip.indexOf(itemType(arr) as string) !== -1) {
        return '';
    }

    const label = opts.label ?? ucwordsAndSpaceTs(layerName);
    const labelHtml = opts.labelHtml ?? false;
    const liClass = opts.liClass ?? 'list-group-item-success fw-bold';
    const contentType: ContentType = opts.contentType ?? 'badge';
    const badgeClass = opts.badgeClass ?? 'bg-dark';
    const customHtml = opts.customHtml ?? '';
    const contentCallback = opts.contentCallback ?? null;
    let extraHtml = opts.extraHtml ?? '';
    if (opts.extraHtmlCallback) extraHtml = opts.extraHtmlCallback(ctx, layerName);

    // bucketed-first slot read (carrier walk via layerOf for the scalar fallback).
    let slotRoot: unknown = layerSlot(arr, layerName);
    if (slotRoot === null || slotRoot === undefined) {
        const carried = layerOf(arr, layerName);
        slotRoot = carried === undefined ? null : carried;
    }
    const rawSlot = Array.isArray(slotRoot) ? (slotRoot[0] ?? slotRoot) : (slotRoot ?? '');
    const valueDisplay = (rawSlot && typeof rawSlot === 'object')
        ? (layerScoringToken(rawSlot) ?? '')
        : (rawSlot === null || rawSlot === false ? '' : (typeof rawSlot === 'string' || typeof rawSlot === 'number' || typeof rawSlot === 'boolean' ? String(rawSlot) : ''));
    const valueNumeric = numericSlot(rawSlot, 0);
    const idForLink = scalarLayerSlot(rawSlot);
    const linkTarget = idForLink !== '' ? idForLink : valueDisplay;

    const headerIcon = headerIconHtml(opts.icon, layerName);
    const sep = afterLabelSep(String(label ?? ''), labelHtml);

    let body = '';
    if (contentCallback) {
        body = contentCallback(ctx, layerName);
    } else if (contentType === 'custom') {
        body = customHtml;
    } else {
        body += labelHtml ? String(label ?? '') : escapeHtml(label);
        if (contentType === 'badge') {
            body += sep + '<span class="badge ' + badgeClass + '">' + escapeHtml(valueDisplay) + '</span>';
        } else if (contentType === 'badge_percent') {
            body += sep + '<span class="badge ' + badgeClass + '">' + (valueNumeric * 100) + '%</span>';
        } else if (contentType === 'badge_percent_suffix') {
            body += sep + '<span class="badge ' + badgeClass + '">' + escapeHtml(valueDisplay) + '%</span>';
        } else if (contentType === 'badge_ft_suffix') {
            body += sep + '<span class="badge ' + badgeClass + '">' + escapeHtml(valueDisplay) + ' ft</span>';
        } else if (contentType === 'badge_multiplier_suffix') {
            body += sep + '<span class="badge ' + badgeClass + '">' + escapeHtml(valueDisplay) + 'x</span>';
        } else if (contentType === 'item_link') {
            body += sep + '<span role="button" onclick="openItemInfo(\'' + escapeHtml(linkTarget) + '\');">' + iconMap(linkTarget) + '</span>';
        } else if (contentType === 'item_link_with_name') {
            const def = resolveEntryFullArr({}, linkTarget, ctx);
            const name = String(itemsCol(def, 'item_name') ?? '') || nameOf(ctx.names, linkTarget);
            const type = String(itemsCol(def, 'item_type') ?? '') || (ctx.names[linkTarget.toUpperCase()]?.item_type ?? '');
            body += sep + '<span role="button" onclick="openItemInfo(\'' + escapeHtml(linkTarget) + '\');">'
                + (runeChipHtml(def, type, linkTarget, name) || iconForRef(ctx.names, linkTarget, type))
                + ' ' + (name ? escapeHtml(name) : unresolvedReferenceHtml(layerName, linkTarget)) + '</span>';
        } else if (contentType === 'label_only') {
            // label already emitted
        } else { // 'text' and default
            body += sep + customHtml;
        }
    }

    return '<div class="card-text ft-ld-block">'
        + '<ul class="list-group border ft-ld-list">'
        + '<li class="list-group-item ft-ld-single-item ' + liClass + '" aria-current="true">'
        + headerIcon + body
        + '</li>'
        + (extraHtml ? extraHtml : '')
        + '</ul>'
        + '</div>';
}

export interface CardListBlockOpts {
    label?: string;
    labelHtml?: boolean;
    headerClass?: string;
    icon?: string | false | null;
    extraHtml?: string;
    extraHtmlCallback?: (ctx: RenderCtx, layer: string) => string;
    /** qty badge applied to every row that lacks its own qty (e.g. spell material consumed qty). */
    entryQtyFallback?: unknown;
    /** Sort entries ascending by their highest requirement (requires_skill_tier_N / requires_level). */
    sortByRequirements?: boolean;
    /**
     * Render-time computed entries to show beside the authored layer without mutating the cached blob.
     * Mirrors PHP's ft_merge_derived_into_render_copy() path.
     */
    derivedEntries?: unknown[];
}

/** Port of render_list_display_card_list_block (read path). */
export function renderCardListBlock(ctx: RenderCtx, layerName: string, cfg: CardListBlockOpts = {}): string {
    const arr = ctx.arr;
    const derivedEntries = Array.isArray(cfg.derivedEntries) ? cfg.derivedEntries : [];
    if (!hasLayer(arr, layerName) && derivedEntries.length === 0) return '';

    const label = cfg.label ?? ucwordsAndSpaceTs(layerName);
    const labelHtml = cfg.labelHtml ?? false;
    const headerClass = cfg.headerClass ?? 'list-group-item-dark border fw-bold';
    const icon = cfg.icon ?? layerName;
    let extraHtml = cfg.extraHtml ?? '';
    if (cfg.extraHtmlCallback) extraHtml = cfg.extraHtmlCallback(ctx, layerName);

    // Authored entries win ordering; computed entries append and dedupe by referenced item id.
    // parseEntryRead understands bare UUIDs, full nests, and computed ability wrappers such as
    // {item_arr, source, built_from:'enchantment'}.
    let entries = [...layerEntriesOf(arr, layerName), ...derivedEntries];
    if (derivedEntries.length > 0) {
        const seen = new Set<string>();
        entries = entries.filter((entry) => {
            const id = String(parseEntryRead(entry)?.id ?? '').toUpperCase();
            if (id === '') return true;
            if (seen.has(id)) return false;
            seen.add(id);
            return true;
        });
    }
    if (entries.length === 0 && extraHtml === '') return '';
    if (cfg.sortByRequirements) {
        // Resolve each entry to its full def via the SAME path the row renderer uses
        // (ctx.fullById + global ftFindFullNestedItem) so the requirement read sees the
        // requires_skill_tier_N layer even when the blob ships this entry as an identity
        // shell and the full def lives outside ctx.arr (e.g. override_ability in side-info).
        entries = sortLayerEntriesByRequirements(entries, arr, entryRequirementResolver(ctx), ctx.formClassLevel);
    }

    const headerLabel = labelHtml
        ? String(label)
        : (escapeHtml(label) + (String(label).replace(/\s+$/, '').endsWith(':') ? '' : ':'));

    return '<div class="card-text ft-ld-block">'
        + '<ul class="list-group border ft-ld-list">'
        + '<li class="list-group-item ' + headerClass + '" aria-current="true">' + iconHtmlForBlock(icon, layerName) + ' ' + headerLabel + '</li>'
        + renderCardListItemLayerRead(ctx, layerName, entries, false, cfg.entryQtyFallback ?? null)
        + (extraHtml ? extraHtml : '')
        + '</ul>'
        + '</div>';
}

/**
 * Render several item-reference layers as one compact list, with each layer's icon identifying
 * its rows. Useful for closely related single-value layers where repeated titled cards add noise.
 */
export interface CompactCardListLayersOpts {
    label?: string;
    icon?: string;
    headerClass?: string;
    pluralize?: boolean;
}

export function renderCompactCardListLayers(ctx: RenderCtx, layerNames: readonly string[], cfg: CompactCardListLayersOpts = {}): string {
    let rows = '';
    let rowCount = 0;
    for (const layerName of layerNames) {
        const entries = layerEntriesOf(ctx.arr, layerName);
        if (entries.length === 0) continue;
        rows += renderCardListItemLayerRead(ctx, layerName, entries, false, null, layerName);
        rowCount += entries.length;
    }
    if (rows === '') return '';

    const baseLabel = cfg.label ?? 'Items';
    const label = baseLabel + ((cfg.pluralize ?? false) && rowCount > 1 ? 's' : '');
    const icon = cfg.icon ?? layerNames[0] ?? '';
    const headerClass = cfg.headerClass ?? 'list-group-item-dark border fw-bold';

    return '<div class="card-text ft-ld-block ft-ld-compact-layers">'
        + '<ul class="list-group border ft-ld-list">'
        + '<li class="list-group-item ' + headerClass + '" aria-current="true">' + iconMap(icon) + ' ' + escapeHtml(label) + ':</li>'
        + rows
        + '</ul>'
        + '</div>';
}

export interface CustomListBlockOpts {
    label?: string;
    headerClass?: string;
    icon?: string | false | null;
    items?: unknown[] | null;
    itemCallback?: (value: unknown, ctx: RenderCtx) => string;
    itemLiClassCallback?: (value: unknown, ctx: RenderCtx) => string;
    extraHtml?: string;
}

/** Port of render_list_display_custom_list_block. */
export function renderCustomListBlock(ctx: RenderCtx, layerName: string, cfg: CustomListBlockOpts = {}): string {
    const arr = ctx.arr;
    let items = cfg.items ?? null;
    const extraHtml = cfg.extraHtml ?? '';
    if (items === null) {
        // Bucket-/carrier-aware (matches render_card_list_block). PHP's custom_list_block
        // still gates top-level isset() here — a latent bug post bucket-reshape that hides
        // bucketed values like `triggers_when_target_uses_item_type: "spell"`. layerEntriesOf
        // resolves the bucket + normalizes scalars→[scalar]/shells/carriers. (PHP helper is
        // patched to match.)
        if (!hasLayer(arr, layerName)) return '';
        items = layerEntriesOf(arr, layerName);
    }
    if ((!items || items.length === 0) && !extraHtml) return '';

    const label = cfg.label ?? ucwordsAndSpaceTs(layerName);
    const headerClass = cfg.headerClass ?? 'list-group-item-dark border fw-bold';
    const icon = cfg.icon ?? layerName;
    const itemCallback = cfg.itemCallback ?? null;
    if (!itemCallback && !extraHtml) return '';

    let rows = '';
    if (itemCallback && items) {
        for (const value of items) {
            const liClass = cfg.itemLiClassCallback ? cfg.itemLiClassCallback(value, ctx) : 'list-group-item';
            rows += '<li class="' + liClass + '">' + itemCallback(value, ctx) + '</li>';
        }
    }

    return '<div class="card-text ft-ld-block">'
        + '<ul class="list-group border ft-ld-list">'
        + '<li class="list-group-item ' + headerClass + '" aria-current="true">' + iconMap(typeof icon === 'string' ? icon : layerName) + ' ' + String(label) + (String(label).replace(/\s+$/, '').endsWith(':') ? '' : ':') + '</li>'
        + rows
        + (extraHtml ? extraHtml : '')
        + '</ul>'
        + '</div>';
}

export interface UserChoiceListOpts {
    label?: string;
    icon?: string;
    headerClass?: string;
    includeBaseLayer?: boolean;
    userChoiceCount?: number;
    userChoiceSuffix?: string;
    pluralize?: 'first' | 'last' | null;
    labelSingularPlural?: [string, string];
    /** PHP 'sort_items' — sort the base-layer entries by requirement level (spell_known / curse_known). */
    sortItems?: boolean;
    /**
     * Sort the base-layer entries ascending by the referenced spell's spell_level. TS-only UX
     * enhancement (innate_spell_per_long_rest) — the PHP include sorts by sort_items_by_requirements,
     * so this intentionally diverges from ?ld_ts=compare for this one list.
     */
    sortBySpellLevel?: boolean;
    /** PHP 'sort_by_name' — sort the base-layer entries A→Z by resolved item name (starting equipment). */
    sortByName?: boolean;
}

/** spell_level of a card-list entry (parses the cell → ability/items arr → spell_level layer). NaN → +Infinity (sorts last). */
function spellLevelOfEntry(value: unknown): number {
    const parsed = parseEntryRead(value);
    if (!parsed) return Number.POSITIVE_INFINITY;
    const lvl = Number(layerValue<string>(layerSlot(parsed.itemArr, 'spell_level')) ?? '');
    return isNaN(lvl) ? Number.POSITIVE_INFINITY : lvl;
}

/**
 * Port of render_list_display_user_choice_list — READ path only
 * (show_user_select_dropdowns === false). Renders the base-layer rows + the
 * "Choose N from:" list for each user_choice_1..N. Dropdown FORMS stay PHP.
 */
export function renderUserChoiceListRead(ctx: RenderCtx, baseLayer: string, cfg: UserChoiceListOpts = {}): string {
    const arr = ctx.arr;
    const suffix = cfg.userChoiceSuffix ?? baseLayer;
    const count = cfg.userChoiceCount ?? 4;

    const baseEntries = layerEntriesOf(arr, baseLayer);
    const ucKeys: string[] = [];
    for (let i = 1; i <= count; i++) ucKeys.push(`user_choice_${i}_${suffix}`);
    // user_choice_* keys are bucketed (get_item_arr.php → pure-bucket); read bucket-first.
    const ucPresent = ucKeys.filter(k => { const s = layerSlot(arr, k); return s !== undefined && s !== null; });

    const includeBase = cfg.includeBaseLayer ?? true;
    const hasBase = includeBase && hasLayer(arr, baseLayer);
    if (!hasBase && ucPresent.length === 0) return '';

    // label + pluralization
    let label = cfg.label ?? ucwordsAndSpaceTs(baseLayer);
    if (cfg.labelSingularPlural) {
        const total = baseEntries.length + ucPresent.length;
        label = total > 1 ? cfg.labelSingularPlural[1] : cfg.labelSingularPlural[0];
    } else if (cfg.pluralize) {
        const total = baseEntries.length + ucPresent.length;
        if (total > 1) {
            const words = label.split(' ');
            if (cfg.pluralize === 'first' && words.length > 0) words[0] += 's';
            else if (cfg.pluralize === 'last' && words.length > 0) words[words.length - 1] += 's';
            label = words.join(' ');
        }
    }
    const icon = cfg.icon ?? baseLayer;
    const headerClass = cfg.headerClass ?? 'list-group-item-success border fw-bold';

    let html = '<div class="card-text ft-ld-block"><ul class="list-group border ft-ld-list">'
        + '<li class="list-group-item ' + headerClass + '" aria-current="true">' + iconMap(icon) + ' ' + label + (label.replace(/\s+$/, '').endsWith(':') ? '' : ':') + '</li>';

    if (hasBase) {
        let ordered = baseEntries;
        if (cfg.sortBySpellLevel) ordered = baseEntries.slice().sort((a, b) => spellLevelOfEntry(a) - spellLevelOfEntry(b));
        else if (cfg.sortItems) ordered = sortLayerEntriesByRequirements(baseEntries, ctx.arr, entryRequirementResolver(ctx), ctx.formClassLevel);
        else if (cfg.sortByName) {
            // Resolve each entry's display name the same way renderCardListItemLayerRead does
            // (items bucket first, then the referenced-names map) so the sort matches what renders.
            // Plain codepoint compare on the lowercased name — NOT localeCompare — so the order
            // matches the PHP path's strcasecmp() byte-for-byte on the (ASCII) catalog and doesn't
            // shift with the viewer's browser locale.
            const entryName = (e: unknown): string => {
                const p = parseEntryRead(e);
                if (!p) return '';
                const n = String(itemsCol<string>(p.itemArr, 'item_name') ?? '') || nameOf(ctx.names, p.id);
                return n.toLowerCase();
            };
            ordered = baseEntries.slice().sort((a, b) => {
                const an = entryName(a), bn = entryName(b);
                return an < bn ? -1 : (an > bn ? 1 : 0);
            });
        }
        html += renderCardListItemLayerRead(ctx, baseLayer, ordered);
    }
    // user_choice_* read rows: "Choose N from: <buttons / link / level-filtered select>"
    const sourceId = String(itemsCol<string>(arr, 'item_id') ?? '');
    for (let i = 1; i <= count; i++) {
        const key = `user_choice_${i}_${suffix}`;
        const slot = layerSlot(arr, key);
        if (slot === undefined || slot === null) continue;
        html += renderUserChoiceReadRow(ctx, Array.isArray(slot) ? slot : [slot], suffix, '', undefined, key, sourceId);
    }
    html += '</ul></div>';
    return html;
}

// count==1 user-choice type-string → catalog page link (render_select_user_choice read branch).
const UC_TYPE_PAGE: Record<string, [string, string]> = {
    skill: ['skills.php', 'Skills'], essence_increase: ['essences.php', 'Essence'], feat: ['feats.php', 'Feats'],
    language: ['languages.php', 'Languages'], pack_boon: ['pact_boons.php', 'Pack Boons'], maneuver: ['maneuvers.php', 'Maneuvers'],
    fighting_style: ['fighting_styles.php', 'Fighting Styles'], eldritch_invocation: ['eldritch_invocations.php', 'Eldritch Invocations'],
    infusion: ['infusions.php', 'Infusions'], trick_shot: ['trick_shots.php', 'Trick Shots'], environment: ['environments.php', 'Environments'],
    creature_type: ['creature_types.php', 'Creature Types'], metamagic: ['metamagics.php', 'Metamagics'],
    instrument: ['instruments.php', 'Instruments'],
    instrument_classification: ['instrument_classifications.php', 'Instrument Classifications'], epic_boon: ['epic_boons.php', 'Epic Boons'],
    sanguine: ['sanguines.php', 'Sanguines'], curse_known: ['curses.php', 'Curses'], menta: ['mentas.php', 'Mentas'],
};
// spell-category user choices route through the PHP spell include (DEFERRED here).
const UC_SPELL_SUFFIXES = new Set(['spell', 'at_will_spell', 'innate_spell_per_long_rest', 'curse', 'menta']);

/**
 * Server-precomputed level-filtered option list for a spell-category user_choice (blob._ft_uc_options[<layer>],
 * stamped by ft_ld_ts_enrich_user_choice_options). Mirrors options/forms/select_user_choice_spells_inc.php:
 * a Tom Select of "<level> | <name>" options that navigates to the picked item on change. Wrapped in an <li>
 * for valid list-group markup (the surrounding renderUserChoiceListRead emits a <ul>). The select is
 * Tom-Select-initialized by the mount glue (index.ts calls initializeTomSelect after a successful paint).
 */
export function renderSpellChoiceSelectRead(layerKey: string, sourceId: string, options: Array<{ item_id?: string; item_name?: string; level?: string }>): string {
    const sid = layerKey + '-' + sourceId + '_';
    let html = '<li class="list-group-item"><select class="ts-select mb-2" id="' + escapeHtml(sid) + '" name="' + escapeHtml(layerKey + '[' + sourceId + '_]') + '" data-width="100%" onchange="openItemInfoValue(\'' + escapeHtml(sid) + '\');">'
        + '<option value="">--SELECT--</option>';
    for (const o of options) {
        const id = String(o.item_id ?? '');
        if (!id) continue;
        const lvl = String(o.level ?? '');
        const name = String(o.item_name ?? '');
        html += '<option data-tokens="' + escapeHtml(lvl + ' ' + name) + '" value="' + escapeHtml(id) + '">' + escapeHtml(lvl + ' | ' + name) + '</option>';
    }
    return html + '</select></li>';
}

/**
 * Loader row standing in for a spell-category user-choice <select> whose option pool isn't on the
 * blob yet. Carries the layer key + the id of the item that DECLARES the spec, which is all
 * ftHydrateUserChoiceOptions (user-choice-hydrate.ts) needs to fetch the pool and swap this row for
 * the real select. An <li> so the surrounding list-group markup stays valid, and the same
 * spinner idiom the object-card islands use for their pending fragments.
 */
export function renderUserChoicePendingRow(layerKey: string, sourceId: string, spec: string): string {
    return '<li class="list-group-item" data-ft-uc-pending'
        + ' data-ft-uc-layer="' + escapeHtml(layerKey) + '"'
        + ' data-ft-uc-spec="' + escapeHtml(spec) + '"'
        + ' data-ft-uc-source="' + escapeHtml(sourceId) + '">'
        + '<div class="d-flex justify-content-center py-2" role="status" aria-live="polite">'
        + '<span class="spinner-border spinner-border-sm text-primary" aria-hidden="true"></span>'
        + '<span class="visually-hidden">Loading choices…</span>'
        + '</div></li>';
}

/**
 * Icon for a "Choose N from:" option button. Renders the compact L1 rune medallion
 * (.ft-rune-catalog .ft-rune-uc — LOD-1 detail, ~50px) built from the option's REAL layers, resolved
 * straight from the blob: the option layers ship FULL-NESTED in the card blob's own catalog now, so
 * no server-side DB pivot is needed. Resolution order — ctx.fullById (buildFullAbilityIndex already
 * indexes every full-nested occurrence, catalog included), then a direct catalog read for any render
 * path that didn't thread ctx.fullById (e.g. the openItemInfo side panel). Falls back to iconMap(id)
 * (the generic "?" glyph) only when the option still ships as a layerless shell (blob not yet
 * full-nested) or the type isn't rune-eligible. The medallion lives inside the button: the footer rune
 * scanner still PAINTS it (it only skips the open-rune-info click-wiring inside a <button>), so the
 * click falls through to the button's openItemInfo.
 */
function choiceOptionIconHtml(ctx: RenderCtx, id: string, name: string): string {
    if (id) {
        const key = String(id).toUpperCase();
        // Resolve the option's full def the SAME cross-root way ability-list rune rows do
        // (resolveEntryFullArr): ctx.fullById (in-blob index incl. this blob's full-nested catalog)
        // → window.ftFindFullNestedItem (the global caster/object/encounter cache finder, which scans
        // EVERY cache root's catalog). The openItemInfo SIDE PANEL resolves its blob via findFullNestedItem
        // too, so its ctx.arr is often a realiased/nested node carrying NO catalog of its own — fullById
        // and ctx.arr.catalog are then both empty and only the global finder can reach the option
        // (it lives in the item.php-seeded full host blob's catalog). [[reference_rune_shell_reresolve]]
        let def = resolveEntryFullArr({}, id, ctx);
        if (!recordOf(def)?.item_layer_types) {
            const cat = ctx.arr.catalog;
            if (cat) def = cat[key] ?? cat[id] ?? def;
        }
        const defRecord = recordOf(def);
        if (defRecord?.item_layer_types) {
            const type = String(recordOf(defRecord.items)?.item_type ?? (ctx.names[id] ?? ctx.names[key])?.item_type ?? '');
            const chip = runeChipHtml(def, type, id, name, 72, 'ft-rune-catalog ft-rune-uc');
            if (chip) return chip;
        }
    }
    return iconMap(id);
}

/**
 * Read-mode user-choice row (render_select_user_choice, show_user_select_dropdowns === false).
 * count>1 → "Choose [N] From:" + sorted option buttons; count==1 type-string → "Choose from: <page link>".
 * Spell-category suffixes (spell/menta/curse/…) render a level-filtered <select> from the server-precomputed
 * blob._ft_uc_options (passed in as spellOptions); when those aren't present they stay DEFERRED (empty), as
 * before the precompute existed. userChoiceNumber defaults '' (no badge).
 */
export function renderUserChoiceReadRow(ctx: RenderCtx, choices: unknown[], suffix: string, userChoiceNumber = '', spellOptions?: Array<{ item_id?: string; item_name?: string; level?: string }>, layerKey = '', sourceId = ''): string {
    if (ctx.renderChoice) return ctx.renderChoice(ctx, choices, suffix, layerKey, sourceId);
    const numBadge = (Number(userChoiceNumber) > 1) ? " <span class='badge bg-dark'>" + escapeHtml(userChoiceNumber) + '</span>' : '';
    if (choices.length > 1) {
        // prefer each choice's own shell name (matches PHP's per-id DB name lookup); else referenced map.
        const named = choices.map(c => {
            const id = (typeof c === 'string') ? c : scalarLayerSlot(c);
            let name = (c && typeof c === 'object') ? String(itemsCol<string>(c, 'item_name') ?? '') : '';
            if (!name) name = isUuid(id) ? nameOf(ctx.names, id) : id;
            return { id, name };
        });
        named.sort((a, b) => a.name.localeCompare(b.name));
        let li = "<li class='list-group-item'><span class='fw-bold text-decoration-underline'>Choose" + (numBadge || " <span class='badge bg-dark'></span>") + ' From:</span><br />';
        for (const { id, name } of named) {
            li += "<button type='button' class='btn btn-primary m-1' onclick=\"openItemInfo('" + escapeHtml(id) + "');\">" + choiceOptionIconHtml(ctx, id, name) + ' ' + escapeHtml(name) + '</button>';
        }
        return li + '</li>';
    }
    if (choices.length === 1) {
        const type = typeof choices[0] === 'string' ? choices[0] : scalarLayerSlot(choices[0]);
        if (UC_SPELL_SUFFIXES.has(suffix)) {
            if (Array.isArray(spellOptions) && spellOptions.length > 0 && layerKey) {
                return renderSpellChoiceSelectRead(layerKey, sourceId, spellOptions);
            }
            // Curated UUID picks need no catalog query. Specs use the page's
            // shared authorized catalog and the browser option selector.
            if (layerKey && isUuid(sourceId) && !isUuid(type)) {
                return renderUserChoicePendingRow(layerKey, sourceId, String(type));
            }
            return '';
        }
        const page = UC_TYPE_PAGE[type];
        if (page) {
            return "<ul class='list-group'><li class='list-group-item'>Choose" + numBadge + ' from: <a href="' + BASE_URL + page[0] + '" class="link-primary" target="_blank">' + page[1] + '</a></li></ul>';
        }
        return "<ul class='list-group'><li class='list-group-item'>Choose" + numBadge + ' from: ' + escapeHtml(ucwordsAndSpaceTs(type)) + '</li></ul>';
    }
    return '';
}

interface ParsedEntry { id: string; itemArr: unknown; qty: unknown; enchantmentQty: unknown; enchantmentTemplateId: string | null; }

/** Read-path port of CardListItemLayer::parseValue (no DB / no enchantment-template fetch). */
function parseEntryRead(value: unknown): ParsedEntry | null {
    let qty: unknown = null;
    let enchantmentQty: unknown = null;

    let enchantmentTemplateId: string | null = null;

    // rich layer cell with item_layer_effect (enchantment): {item_layer_effect, template_id}.
    const valueRecord = recordOf(value);
    if (valueRecord && 'item_layer_effect' in valueRecord && !('ability_id' in valueRecord)) {
        const effectId = scalarLayerSlot(value);
        if (!effectId || !isUuid(effectId)) return null;
        const tid = valueRecord.template_id;
        return { id: effectId, itemArr: value, qty: valueRecord.qty ?? null, enchantmentQty: null, enchantmentTemplateId: (typeof tid === 'string' && isUuid(tid)) ? tid : null };
    }

    // Enchantment-granted ability entries (computed.enchantment.abilities.*, merged into the ability
    // sections by renderAbilityListRead) carry {source: <enchantment_template_id>, built_from:
    // 'enchantment'}. Surface the template so the per-row "| <Enchantment>" tag links back to it
    // (mirrors CardListItemLayer::parseValue). Flows through every return below.
    if (valueRecord
        && valueRecord.built_from === 'enchantment'
        && typeof valueRecord.source === 'string' && isUuid(valueRecord.source)) {
        enchantmentTemplateId = valueRecord.source;
    }

    let v: unknown = value;
    if (typeof v === 'string' && v.indexOf('|') !== -1) {
        const parts = v.split('|');
        v = parts[0];
        qty = parts[1] ?? null;
        if (parts[2] !== undefined) enchantmentQty = parts[2];
    }
    if (typeof qty === 'string' && isUuid(qty)) {
        enchantmentTemplateId = qty; // "uuid|<template-uuid>" form — qty position holds the template id
        qty = null;
    }

    if (typeof v === 'string') {
        return { id: v, itemArr: { items: { item_id: v } }, qty, enchantmentQty, enchantmentTemplateId };
    }
    // Structured creature-defense entry — the CANONICAL store shape at
    // computed.creature.defenses.{resistance,immune,vulnerable}: { damage_type: <uuid>, source_*?,
    // all_sources? } (written by add_damage_type_defense_entry, creature_finalize_helpers.php). The
    // FT_CREATURE_DEFENSE_SLOTS bridge in layerSlot surfaces these for damage_type_resistance /
    // _immunity / _vulnerability, so hasLayer is true and the section HEADER renders — but without this
    // branch every entry parsed to null and the rows were dropped, leaving an empty "Damage Type
    // Resistance:" card in the side-info / object-card (creature/caster-folded blob). Render the
    // damage_type as the row item, exactly like a bare damage_type UUID. Mirror of
    // CardListItemLayer::parseValue's damage_type branch (additions.php).
    const vRecord = recordOf(v);
    if (vRecord && typeof vRecord.damage_type === 'string' && isUuid(vRecord.damage_type)) {
        const dt = vRecord.damage_type;
        return { id: dt, itemArr: { items: { item_id: dt } }, qty, enchantmentQty, enchantmentTemplateId };
    }
    if (vRecord && 'ability_id' in vRecord) {
        // item_arr may be a bare-UUID catalog ref (v21; renamed to item_id in v23 #5) — normalize
        // to a minimal identity node so downstream layer/name reads (which resolve via
        // names-map/catalog) don't choke on a string.
        let entryItemArr: unknown = vRecord.item_arr
            ?? (typeof vRecord.item_id === 'string' ? vRecord.item_id : v);
        if (typeof entryItemArr === 'string') {
            entryItemArr = { items: { item_id: entryItemArr } };
        }
        return { id: String(vRecord.ability_id), itemArr: entryItemArr, qty, enchantmentQty, enchantmentTemplateId };
    }
    // Full-nest ability entry WITHOUT ability_id: {item_arr:{...}, source, built_from} — the shape
    // build_enchantment_template_arr emits in a FRESH compile (computed.enchantment.abilities.*; also
    // computed.creature derived nests). item.php's mount ships this raw, while the side-info / cache
    // seed leans it to a bare {item_id} (handled below). Mirror PHP parseValue's item_arr branch so
    // the live mount renders rows, not just the lean cache — otherwise the section header shows with
    // zero rows (the gate counts the entries but each parses to null).
    if (vRecord && vRecord.item_arr && typeof vRecord.item_arr === 'object') {
        const inner = vRecord.item_arr;
        const innerId = String(itemsCol<string>(inner, 'item_id') ?? '');
        if (innerId) return { id: innerId, itemArr: inner, qty, enchantmentQty, enchantmentTemplateId };
    }
    const vItems = recordOf(vRecord?.items);
    if (vRecord && typeof vItems?.item_id === 'string') {
        return { id: vItems.item_id, itemArr: v, qty, enchantmentQty, enchantmentTemplateId };
    }
    // ROUND-6 #1: an inventory INSTANCE whose items pointer bucket was dropped — the template
    // id rides inventory.inventory_item_id.
    const vInventory = recordOf(vRecord?.inventory);
    if (vRecord && vInventory) {
        let invTid = vInventory.inventory_item_id;
        if (Array.isArray(invTid)) invTid = invTid[0];
        if (typeof invTid === 'string' && invTid !== '') {
            return { id: invTid, itemArr: v, qty, enchantmentQty, enchantmentTemplateId };
        }
    }
    if (vRecord && typeof vRecord.item_id === 'string') {
        // v23 #5 renamed ability wrapper ({item_id: uuid} with no embedded identity): hand back
        // an identity node (names/layers resolve via names-map/catalog), not the layerless wrapper.
        const itemArr = (vRecord.item_name === undefined && vRecord.items === undefined && isUuid(vRecord.item_id))
            ? { items: { item_id: vRecord.item_id } }
            : v;
        return { id: vRecord.item_id, itemArr, qty, enchantmentQty, enchantmentTemplateId };
    }
    return null;
}

function costGpOf(itemArr: unknown): number | null {
    const raw = layerValue<unknown>(layerSlot(itemArr, 'cost_gp'));
    if (raw === null || raw === undefined || raw === '') return null;
    return numericSlot(raw, 0);
}

// Item types that get a rune chip — mirrors PHP ft_rune_icon_eligible (spell + AbilityType::LIST
// minus the ability_choice selector wrapper). Kept in sync with includes/additions.php AbilityType.
const RUNE_ELIGIBLE_TYPES = new Set([
    'spell', 'passive_ability', 'full_ability', 'ability', 'quick_ability', 'attack_ability',
    'free_ability', 'reaction_ability', 'opportunity_attack_ability', 'curse', 'menta', 'sanguine',
    'lair_ability',
]);

// Creature-defense entries can arrive as bare damage-type UUIDs. Their names/icons still resolve
// from the static registries, but the stripped entry may not carry item_type === 'damage_type'.
// These three list sections are definitionally damage-type references, so always paint the
// referenced damage icon beside the row name instead of depending on the optional type shell.
const DAMAGE_TYPE_DEFENSE_LAYERS = new Set([
    'damage_type_resistance',
    'damage_type_immunity',
    'damage_type_vulnerability',
]);

/**
 * Rune-chip placeholder rendered to the LEFT of an item name in list rows. Mirrors the PHP
 * ft_rune_icon_mount_html: emits the same .ft-rune-mount markup carrying a {items, item_layer_types}
 * payload as a non-executable <script type="application/json"> (survives innerHTML), which the
 * existing window.ftMountRuneIcons() scanner hydrates into a rune SVG. We emit the placeholder
 * rather than calling window.RuneIcon directly because the rune bundle loads AFTER this one in the
 * footer — the scanner runs later (DOMContentLoaded / after ftMountListDisplays).
 *
 * Two presentations (both hydrated by the same scanner, sized by the matching css/style.css class):
 *   - L0 chip  (size 22, .ft-rune-inline) — the compact ~3em sigil for generic rune-eligible rows.
 *   - L1 medallion (size 72 → LOD 1, .ft-rune-catalog, 64px on screen) — the richer "more detail"
 *     medallion for ability-list rows, mirroring the catalog NAME-column rune (ft_rune_icon_catalog_html).
 *
 * Returns '' for ineligible types or rows with no layers (a typeless/layerless rune is meaningless).
 */
function runeChipHtml(itemArr: unknown, type: string, id: string, name: string, size = 22, cls = 'ft-rune-inline'): string {
    if (!RUNE_ELIGIBLE_TYPES.has(type)) return '';
    const itemRecord = recordOf(itemArr);
    const ilt = recordOf(itemRecord?.item_layer_types);
    if (!ilt) return '';
    // Guarantee the rune knows its identity even for legacy/bare shells whose items bucket is thin.
    const items = { item_id: id, item_name: name, item_type: type, ...(recordOf(itemRecord?.items) ?? {}) };
    const json = JSON.stringify({ items, item_layer_types: ilt, catalog: recordOf(itemRecord?.catalog) ?? {} }).replace(/<\//g, '<\\/');
    return '<span class="ft-rune-mount ' + cls + '" data-rune-size="' + size + '">'
        + '<script type="application/json" class="ft-rune-blob">' + json + '</script>'
        + '</span>';
}

/**
 * Resolve a list entry to its FULL def (one carrying real item_layer_types) so the rune chip and
 * every layer-dependent per-row badge (spell circle/level, requires_level, requires_class icons …)
 * render from real data. Two stages:
 *   1. ctx.fullById — the in-blob index (a dedup/skill-derived ability shells to its ONE full copy
 *      elsewhere in the SAME blob). Pure blob lookup, no re-query.
 *   2. ftFindFullNestedItem — the GLOBAL finder, only when still a layerless shell. A class/subclass
 *      card's granted spells/abilities are bare refs whose full defs live in the CASTER root
 *      `.catalog` (not in the class blob's fullById); the finder pulls them from there. Memoized +
 *      catalog-fast-pathed (no per-row walk); returns null when nothing's cached (plain item.php
 *      page) → no change there. Strictly additive: it only ever UPGRADES a layerless shell.
 */
function resolveEntryFullArr(itemArr: unknown, id: string, ctx: RenderCtx): unknown {
    const fullCopy = id ? ctx.fullById?.[String(id).toUpperCase()] : undefined;
    if (fullCopy && fullCopy !== itemArr) {
        const curN = Object.keys(recordOf(recordOf(itemArr)?.item_layer_types) ?? {}).length;
        // Guard the fullCopy read too (Fable 2026-07-01): a LAYERLESS interned shell in fullById
        // made Object.keys(undefined) THROW here, killing the whole render mid-sort and silently
        // dropping the cache-first paint to the server path. Layerless copy → count 0 → no upgrade.
        const fullN = Object.keys(recordOf(fullCopy.item_layer_types) ?? {}).length;
        if (fullN > curN) itemArr = fullCopy;
    }
    const curLayers = Object.keys(recordOf(recordOf(itemArr)?.item_layer_types) ?? {}).length;
    const finder = window.ftFindFullNestedItem;
    if (curLayers === 0 && id && typeof finder === 'function') {
        try {
            const g = finder(id);
            if (Object.keys(recordOf(recordOf(g)?.item_layer_types) ?? {}).length > 0) {
                itemArr = g;
            }
        } catch (e) { /* finder not ready → leave the shell */ }
    }
    return itemArr;
}

// Inline a base item's *_classification carrier ref (a bare-UUID catalog ref) with its FULL def from
// the blob's OWN root `.catalog`, returning a shallow copy whose carrier slot holds the full object so
// the picture carrier-walk descends it directly instead of via window.ftFindFullNestedItem / a warm
// objectCache (cold on a fresh item.php first paint). No-op without a catalog or a bare-UUID carrier.
// Per-entry twin of resolveClassificationCarriersFromCatalog (render-item-list-display.ts).
const FT_CLASSIFICATION_CARRIER_LAYERS = ['weapon_classification', 'armor_classification', 'instrument_classification', 'weapon_ammo_classification'];
function inlineClassificationCarriers(def: unknown, catalog: unknown): unknown {
    const defRecord = recordOf(def);
    const ilt = recordOf(defRecord?.item_layer_types);
    const catalogRecord = recordOf(catalog);
    if (!defRecord || !ilt || !catalogRecord) return def;
    let touched = false;
    const next: Record<string, unknown> = { ...ilt };
    const resolve = (ref: unknown): unknown => {
        if (typeof ref !== 'string' || ref === '') return ref;
        const c = catalogRecord[ref] || catalogRecord[ref.toUpperCase()] || catalogRecord[ref.toLowerCase()];
        const carrier = recordOf(c);
        if (carrier && (carrier.items || carrier.item_layer_types)) { touched = true; return c; }
        return ref;
    };
    for (const k of FT_CLASSIFICATION_CARRIER_LAYERS) {
        const slot = next[k];
        if (Array.isArray(slot)) next[k] = slot.map(resolve);
        else if (typeof slot === 'string' && slot !== '') next[k] = resolve(slot);
    }
    return touched ? { ...defRecord, item_layer_types: next } : def;
}

/**
 * Entry→full-def resolver for sortLayerEntriesByRequirements. Resolves each list entry — which the
 * blob may ship as an identity SHELL (the dedup pass keeps every ability full-nested ONCE and shells
 * the rest) — to its full def via the SAME path the row renderer uses (parseEntryRead →
 * resolveEntryFullArr: ctx.fullById, then the global ftFindFullNestedItem). Without it the sort reads
 * the requirement layers (requires_skill_tier_N / requires_level / requires_class_level) off the shell,
 * where they're absent, and silently sorts every row to 0 — i.e. authoring order, NOT requirement
 * order. Share it across every list that sorts by requirement (ability sections, override_ability, …)
 * so the SORT reads the same node the BADGES do and matches the PHP sort_items_by_requirements render.
 */
export function entryRequirementResolver(ctx: RenderCtx): (entry: unknown) => unknown {
    return (entry: unknown): unknown => {
        const parsed = parseEntryRead(entry);
        if (!parsed) return entry;
        return resolveEntryFullArr(parsed.itemArr, parsed.id, ctx);
    };
}

/**
 * Read-mode card_list_item_layer row renderer — full parity with the PHP read path
 * (show_user_select_dropdowns === false, no ability button / no encounter creature):
 * thumbnail (inventory) / icon-before-name + qty + name, then pl / cost_gp / spell
 * casting-time+level+circle / requires_class_level icons / requires_level / skill-tier
 * badges. Enchantment-template links (DB build) are omitted (rare in read view).
 */
/** For a static item-creature's enchanted equipped gear: the inventory_equipped LAYER entry is
 *  {item_layer_effect: BASE_UUID, template_id: TPL_UUID}, but the SERVER also projects a synthetic,
 *  already-enchantment-composed inventory node into ctx.arr.inventory (enchantment_and_inventory_
 *  layering.php, marked inventory_static_display). Opening the bare BASE_UUID shows the unenchanted
 *  item; opening the synthetic node's inventory_id shows the enchanted one (rendered read-only from
 *  cache — see render-from-cache.ts). Return that synthetic inventory_id, or null when there's no match
 *  (then the caller keeps the base id — unchanged behavior). */
function findStaticEquippedSyntheticId(casterArr: unknown, baseItemId: string, templateId: string): string | null {
    const inv = recordOf(casterArr)?.inventory;
    if (!Array.isArray(inv)) return null;
    const baseU = baseItemId.toUpperCase();
    const tplU = templateId.toUpperCase();
    for (const node of inv) {
        const b = recordOf(recordOf(node)?.inventory);
        if (!b) continue;
        const sd = b.inventory_static_display;
        if (sd !== 1 && sd !== '1' && sd !== true) continue;
        if (String(b.inventory_item_id ?? '').toUpperCase() !== baseU) continue;
        if (String(b.enchantment_template_id ?? '').toUpperCase() !== tplU) continue;
        const sid = String(b.inventory_id ?? '');
        if (sid !== '') return sid;
    }
    return null;
}

export function renderCardListItemLayerRead(ctx: RenderCtx, layerName: string, entries: unknown[], sheetAbilitySearch = false, qtyFallback: unknown = null, leadingIcon: string | null = null): string {
    if (ctx.renderRows) return ctx.renderRows({ ...ctx, renderRows: undefined }, layerName, entries, sheetAbilitySearch, qtyFallback, leadingIcon);
    let out = '';
    for (const value of entries) {
        const parsed = parseEntryRead(value);
        if (!parsed) continue;
        const { id, qty, enchantmentQty, enchantmentTemplateId } = parsed;
        let itemArr = parsed.itemArr;

        // SideInfo click target: for enchanted equipped gear, open the server-projected synthetic
        // (enchantment-composed) node instead of the bare base item, so the enchantment shows. Falls
        // back to the base id when no synthetic node matches. Display (name/pic/badges) keeps using `id`.
        // Enchanted equipped gear: open the server-projected synthetic (enchantment-composed) node
        // instead of the bare base item, so the enchantment shows. Resolves now that the static-creature
        // page seeds casterCache (table_creature_inc.php). inventory_equipped ONLY — the server projects
        // synthetic nodes for EQUIPPED gear only. Falls back to the base id when no synthetic node matches.
        let clickId = id;
        if (layerName === 'inventory_equipped' && enchantmentTemplateId) {
            const syntheticId = findStaticEquippedSyntheticId(ctx.arr, id, enchantmentTemplateId);
            if (syntheticId) clickId = syntheticId;
        }

        // SHELL → FULL resolution (in-blob fullById, then the global catalog finder for class/subclass
        // ref entries) so the rune + every per-row badge render from real layer data. See resolveEntryFullArr.
        itemArr = resolveEntryFullArr(itemArr, id, ctx);

        // Server-precomputed enchantment/armor display sugar stamped by ft_ld_ts_enrich_inventory
        // (the client can't re-query the base item or fetch the enchantment template):
        //   { cost_gp (base × multipliers), pic_url (classification art), enchantment_template_name }
        const displayRecord = recordOf(recordOf(itemArr)?._ft_display);
        const disp = displayRecord as { cost_gp?: number | null; pic_url?: string | null; enchantment_template_name?: string | null } | null;

        // name + type from the entry's items bucket, else referenced-names map.
        let name = String(itemsCol<string>(itemArr, 'item_name') ?? '');
        let type = String(itemsCol<string>(itemArr, 'item_type') ?? '');
        if (!name) name = nameOf(ctx.names, id);
        if (!type) type = (ctx.names[id] ?? ctx.names[id.toUpperCase()])?.item_type ?? '';

        // Keep the labelled marker until deferred catalog hydration can name the reference.
        // Missing/deleted/unreadable identities retain it after the read too.
        const nameUnresolved = name === '';
        const nameHtml = nameUnresolved ? unresolvedReferenceHtml(layerName, id) : name;

        const requiresLevel = (hasLayer(itemArr, 'requires_level') && HIDE_REQUIRES_LEVEL.indexOf(layerName) === -1)
            ? layerValue<string>(layerSlot(itemArr, 'requires_level')) : null;

        // display qty: enchantment_qty if numeric, else the entry's own qty, else the
        // block-level fallback (e.g. spell_component_material_consumed.qty applies to every
        // spell_material_item_id row — the consumed amount isn't stored per material entry).
        let displayQty: string | null = null;
        if (enchantmentQty !== null && enchantmentQty !== undefined && !isNaN(Number(enchantmentQty))) displayQty = formatQty(enchantmentQty);
        else if (qty !== null && qty !== undefined && !isNaN(Number(qty))) displayQty = formatQty(qty);
        else if (qtyFallback !== null && qtyFallback !== undefined && qtyFallback !== '' && !isNaN(Number(qtyFallback))) displayQty = formatQty(qtyFallback);

        // ── leading cell ──
        let leading = '';
        if (leadingIcon) {
            leading += '<span class="flex-shrink-0 mr-2 ft-ld-layer-icon">' + iconMap(leadingIcon) + '</span>';
        }
        if (layerName === 'inventory_equipped' || layerName === 'inventory_unequipped') {
            // A base weapon/armor's art lives on its *_classification carrier (a bare-UUID ref); inline
            // it from the blob's OWN catalog so primaryPictureOf's carrier-walk resolves directly rather
            // than via window.ftFindFullNestedItem / a cold objectCache (a fresh item.php first paint),
            // which had left base weapons/armor pictureless. No-op for items with their own picture.
            const pic = primaryPictureOf(inlineClassificationCarriers(itemArr, ctx.arr.catalog));
            // Enriched pic is authoritative (list_display_inventory_thumbnail_url walks the
            // armor_classification carrier, so Medium Shield / Plate etc. get their art). Then
            // the entry's own pictures, then the referenced item's pic_url from the names map.
            const enrichedPic = (disp && disp.pic_url) ? String(disp.pic_url) : null;
            const refPic = (ctx.names[id] ?? ctx.names[id.toUpperCase()])?.pic_url;
            const ownPicUrl = pic ? pic.picture_url : null;
            const thumbUrl = enrichedPic ?? (ownPicUrl ? String(ownPicUrl) : (refPic ? String(refPic) : null));
            leading += '<div class="flex-shrink-0 mr-3 d-flex align-items-center justify-content-center" style="width: 40px; height: 40px;">';
            leading += thumbUrl !== null
                ? '<img src="' + escapeHtml(thumbUrl) + '" alt="" width="40" height="40" class="rounded border border-secondary" style="width: 40px; height: 40px; object-fit: cover; background-color: #1b1f24;" loading="lazy">'
                : '<span class="fs-4">' + iconMap(type) + '</span>';
            leading += '</div>';
        }

        // Rune medallion (L1/72px) in its OWN flex column for ANY rune-eligible row — spells, curses,
        // menta, sanguine, and every *_ability — not just the *_ability sections. Driven by eligibility
        // (runeChipHtml self-gates on RUNE_ELIGIBLE_TYPES), so all rune lists share the same
        // [rune | name | badges] layout; non-rune rows (inventory/skill/damage_type) get no medallion
        // and keep their own thumbnail/type icon. It lives in its own flex-shrink-0 sibling (NOT inline
        // in the name cell — inline, the name wrapped UNDERNEATH the medallion in the narrow panel). The
        // medallion is a BORDERLESS <button> (.ft-ability-icon-btn) so the inner rune is inert to the
        // footer scanner (no openRuneInfo hijack) and carries the BS name tooltip; the click bubbles to
        // the row's openItemInfo.
        const runeMedallion = runeChipHtml(itemArr, type, id, name, 72, 'ft-rune-catalog');
        const isRuneRow = runeMedallion !== '';
        // Per user choice: rune rows also carry the ability-search hook (fate-tide-sheet-ability-row +
        // data-ft-ability-name) so the sheet search box covers spells/curses too, not just *_ability
        // rows. Inert without a search box.
        const abilitySearchRow = sheetAbilitySearch || isRuneRow;
        if (isRuneRow) {
            leading += '<div class="flex-shrink-0 mr-2 d-flex align-items-center">'
                + '<button type="button" class="ft-ability-icon-btn" data-bs-toggle="tooltip" data-bs-placement="top" data-bs-animation="false" data-bs-original-title="' + escapeHtml(name) + '">' + runeMedallion + '</button>'
                + '</div>';
        }

        // ── name cell ── (rune-eligible rows use the medallion column above; non-rune rows have no rune)
        // Does this row already carry a glyph? (leading thumbnail/icon column, or the reagent icon
        // below.) Gates the typeless-row icon fallback further down so it never doubles up.
        const hasOwnGlyph = leading !== '' || layerName === 'requires_reagent';
        // Permanent-status rows pair a bespoke status icon with the status name. Keep that pair in
        // one centered flex cell so differently-shaped icons (for example Chilled's snowflake) do
        // not sit on the text baseline while the PL badge is centered on the other side.
        const nameCellClass = layerName === 'status_effect_permanent'
            ? 'flex-grow-1 d-flex align-items-center'
            : 'flex-grow-1';
        let nameCell = '<div class="' + nameCellClass + '">';
        if (layerName === 'requires_reagent') nameCell += '<span class="pr-1">' + iconMap(type) + '</span>';
        const showReferenceIcon = DAMAGE_TYPE_DEFENSE_LAYERS.has(layerName)
            || ICON_BEFORE_NAME_ITEM_TYPES.indexOf(type) !== -1
            || layerName.startsWith('roll_type_');
        if (showReferenceIcon) nameCell += '<span class="ft-ld-row-icon">' + iconForRef(ctx.names, id, type) + '</span>';
        // Any other plain reference row (creature type / sub-type / environment / weather / light type /
        // sphere / weapon type …) whose id carries its OWN registered icon gets it beside the name too, so
        // lists like "Ability only works against creature type: Undead" aren't bare text. Rows that
        // already have a glyph (rune medallion, inventory thumbnail, leadingIcon, reagent) are skipped.
        else if (!isRuneRow && !hasOwnGlyph && !nameUnresolved && refHasOwnIcon(ctx.names, id)) {
            nameCell += '<span class="ft-ld-row-icon">' + iconForRef(ctx.names, id, type) + '</span>';
        }
        // A reference we could not TYPE gets neither the rune medallion (runeChipHtml gates on
        // item_type) nor the icon-before-name gate above, so it would paint as bare text on a bare
        // line. Give it the LAYER's own icon — the same glyph as the section header — so the row
        // still reads as a row. Covers both the fully-unresolved case and the name-only case (a
        // choice named from `ability_choice_options`, whose def isn't in this blob to type it).
        // Only when the row has NO glyph of its own yet: a rune medallion, an inventory thumbnail
        // cell, a caller-supplied leadingIcon or the requires_reagent icon all already occupy that
        // slot, and painting a second one there would be a visible regression on rows that render
        // correctly today. And only for the two cases this fix is about — an unresolvable reference,
        // or an ability_choice named from `ability_choice_options` (which carries no item_type).
        // NOT every typeless row: SKILL_CATALOG deliberately registers skills with item_type '' (see
        // namesFromStaticRegistries), so a blanket typeless rule would add an icon to skill rows that
        // resolve perfectly well today.
        else if (
            type === '' && !isRuneRow && !hasOwnGlyph
            && (nameUnresolved || layerName === 'ability_choice')
        ) nameCell += '<span class="ft-ld-row-icon">' + iconMap(layerName) + '</span>';
        if (displayQty !== null) nameCell += '<span class="badge bg-dark border border-light">' + displayQty + '</span> ';
        nameCell += nameHtml; // PHP echoes raw (unescaped) item_name; the fallback is our own markup
        nameCell += '</div>';

        // ── right badge cell ──
        // Rune rows (spells/curses/abilities) carry a fat badge run (casting time + level + circle rune
        // + requires_class_level + unenchantable …). Beside the 64px medallion in the narrow side-info
        // panel there isn't room for [medallion | name | badges] on one line, so for rune rows the badge
        // run drops onto ITS OWN line UNDER the name (stacked in the content column) — left-aligned,
        // wrap-enabled. Non-rune rows (inventory) keep the original inline layout: badges pinned right on
        // one line, name (flex-grow-1) absorbing the squeeze. min-width:0 lets either shrink so the wrap
        // can trigger; the rune medallion column stays full-size (the runic icon system is preserved).
        const rightCls = isRuneRow
            ? 'ft-ld-row-badges ft-ld-row-badges--stacked d-flex align-items-center flex-wrap mt-1'
            : 'ft-ld-row-badges d-flex align-items-center flex-wrap justify-content-end';
        let right = '<div class="' + rightCls + '" style="min-width:0;">';
        // Enchantment provenance: a clickable `enchantment_template` icon (opens the template in a new
        // tab) sits to the LEFT of the pl/cost badges — replaces the verbose "| <Enchantment name>"
        // link, which crowded the ability name. The (often long) name rides a hover tooltip instead.
        // stopPropagation so the icon click doesn't also fire the row's openItemInfo.
        if (enchantmentTemplateId) {
            // Name sources, best first. The ENTRY's own computed.enchantment is what makes this work on
            // the cache-first path: _ft_display is stamped only by the two PHP mounts, and
            // ft_build_referenced_names_map deliberately skips template_id / enchantment_template_applied
            // ("template-table ids, not items — the name is precomputed in _ft_display"), so on a cached
            // node BOTH of the old sources were empty and the icon rendered with no tooltip name.
            // Inventory instances are never catalogized, so their `computed` survives finalize intact.
            const ench = recordOf(ctx.arr.computed?.enchantment);
            const entryEnch = recordOf(recordOf(itemArr)?.computed)?.enchantment;
            const entryEnchRecord = recordOf(entryEnch);
            const tName = (ench && ench.enchantment_template_id === enchantmentTemplateId && ench.item_name)
                ? String(ench.item_name)
                : ((disp && disp.enchantment_template_name)
                    ? String(disp.enchantment_template_name)
                    : ((entryEnchRecord
                        && entryEnchRecord.enchantment_template_id === enchantmentTemplateId
                        && entryEnchRecord.item_name)
                        ? String(entryEnchRecord.item_name)
                        : nameOf(ctx.names, enchantmentTemplateId)));
            right += '<a class="link-primary ml-1" target="_blank" href="' + BASE_URL + 'item.php?id=' + escapeHtml(enchantmentTemplateId) + '" onclick="event.stopPropagation()"'
                + '>' + iconMap('enchantment_template', 16, 16, tName || '') + '</a>';
        }
        // Enriched cost (enchanted = base × cost_multipliers) wins over the entry's own cost_gp.
        const enrichedCost = (disp && disp.cost_gp !== undefined && disp.cost_gp !== null) ? Number(disp.cost_gp) : null;
        // …and when _ft_display is absent — ALWAYS on the cache-first side-info render, which has no PHP
        // mount to stamp it — fall back to the node's OWN computed.cost.total_gp before the raw cost_gp
        // layer. That computed total is the compiled, enchantment-aware figure (measured on staging: an
        // enchanted Spell Scroll reads computed.cost.total_gp = 350.1 while its base cost_gp layer is
        // 0.1), so without this step an enchanted row in the drawer advertised the UNENCHANTED price.
        // It is the same figure the object card's own header badge uses (computedCostTotalGp), so the
        // row and the card it opens now agree. Inventory instances are never catalogized, so `computed`
        // survives finalize — no bake and no extra fetch needed.
        // Guarded to a POSITIVE total on purpose: an absent/zero computed.cost must fall through to the
        // old reader, so this can only ever correct a wrong price — it can never introduce a "0 gp"
        // badge on a row that previously showed none.
        const computedCost = numericSlot(computedValue(itemArr, 'cost', 'total_gp'), NaN);
        const cost = enrichedCost ?? ((!isNaN(computedCost) && computedCost > 0) ? computedCost : costGpOf(itemArr));
        if (cost !== null) right += '<span class="badge bg-warning text-dark rounded-pill ml-1">' + cost + ' gp</span>';
        if (type === 'spell' || type === 'curse') {
            right += '<span class="ml-2 mr-2">' + displaySpellCastingTime(itemArr) + '</span>'
                + '<span class="badge bg-dark border border-light mr-2">' + escapeHtml(layerValue<string>(layerSlot(itemArr, 'spell_level')) ?? '') + '</span>'
                + iconMap(layerValue<string>(layerSlot(itemArr, 'spell_circle')) ?? '');
        }
        right += showRequiresClassLevelIcons(itemArr);
        if (requiresLevel !== null && requiresLevel !== undefined) right += '<span class="badge bg-info ml-1">' + escapeHtml(requiresLevel) + '</span>';
        right += skillTierBadges(itemArr);
        right += '</div>';

        // Rune rows stack name-over-badges in a single grow column beside the medallion; non-rune rows
        // keep name (grow) and badges (right) as siblings on one line.
        const content = isRuneRow
            ? '<div class="flex-grow-1 d-flex flex-column" style="min-width:0;">' + nameCell + right + '</div>'
            : nameCell + right;

        const searchCls = abilitySearchRow ? ' fate-tide-sheet-ability-row' : '';
        const searchAttr = abilitySearchRow ? ' data-ft-ability-name="' + escapeHtml(String(name).toLowerCase()) + '"' : '';
        out += '<li class="list-group-item list-group-item-action' + searchCls + '"' + searchAttr
            + ' data-ft-reference-id="' + escapeHtml(id) + '" data-ft-reference-layer="' + escapeHtml(layerName) + '"'
            + ' onclick="openItemInfo(\'' + escapeHtml(clickId) + '\')" role="button">'
            + '<div class="d-flex align-items-center">'
            + leading + content
            + '</div>'
            + '</li>';
    }
    return out;
}

// REMOVED 2026-07-28: renderAbilityIconStripRead — the icon-only twin of renderCardListItemLayerRead
// (one clickable rune medallion per entry). Its sole caller was renderAbilityIconStrip in
// render-item-list-display.ts, which itself had no callers, so the whole icon-view branch of the
// list display was unreachable. The PC sheet's own medallion strip (ability-section.ts) is a
// separate implementation and is unaffected.

// header icon for card_list_block (icon === false/'' → '' ; else iconMap)
function iconHtmlForBlock(icon: string | false | null, layerName: string): string {
    if (icon === false || icon === '') return '';
    return iconMap(typeof icon === 'string' ? icon : layerName);
}
